import { createBrowserHistory } from 'history';
import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { routerMiddleware } from 'connected-react-router';
import Url from 'url';
import { promiseMiddleware } from './middleware/promise-middleware';
import appReducers from './reducers';
import { getHref, getOrigin, notNull } from './utils';
import { SpaceRecord } from './records/index';
import { Map } from 'immutable';
import window from 'window-or-global';
import memoize from 'lodash/memoize';
import { POPULAR, RECENT } from './constants/filters';

export const getParams = () => {
	const href = getHref();
	const params = Url.parse(href, true).query;
	const { contentType, contentId, contentPlugin } = params;

	return { contentType, contentId, contentPlugin };
};

const getInitialState = (initialStateConfig) => {
	const { spaceKey, filter } = initialStateConfig;

	const initialSpaces = {
		[spaceKey]: SpaceRecord({ spaceKey, watching: null }),
	};

	return {
		entities: {
			spaces: Map(initialSpaces),
		},
		ui: {
			filter: Map({
				result: filter,
			}),
		},
		...{ config: initialStateConfig },
	};
};

export const configureStoreInternal = ({
	initialPath,
	initialStateConfig,
	composeEnhancers,
	appReducers,
}) => {
	const initialState = getInitialState(initialStateConfig);
	// Create an enhanced history that syncs navigation events with the store.
	const history = createBrowserHistory();
	if (initialPath) {
		history.push(initialPath);
	}
	const middleware = applyMiddleware(thunk, promiseMiddleware(), routerMiddleware(history));

	composeEnhancers = composeEnhancers || compose;

	//  Application Bootstrap:
	//  - setup Redux store.
	//  - setup Redux router connection (stores routes inside of store)
	//  - setup Redux dev tools if extension is available/installed.
	const store = createStore(appReducers(history), initialState, composeEnhancers(middleware));

	if (module.hot) {
		// Enable Webpack hot module replacement for reducers
		module.hot.accept('./reducers', () => {
			const nextRootReducer = require('./reducers').default;
			store.replaceReducer(nextRootReducer);
		});
	}

	return {
		store,
		history,
	};
};

export const getAppConfig = memoize(() => {
	//  Extra config being picked up from query params and body metadata.
	const body = notNull(window.document.body, 'Page missing required body');
	const initialAddonTokenHeader = body.getAttribute('addon-token-header');
	const tokenEarlyExpiry = parseInt(body.getAttribute('token-early-expiry'), 10);
	const mentionsUrl = body.getAttribute('data-mentions-url');
	const mentionsProductId = body.getAttribute('data-mentions-product-id');
	const spaceKey = body.getAttribute('data-space-key');
	const isGlobalQuestionsPage = body.getAttribute('data-is-global-questions-page') === 'true';
	const globalSpaceMissing = body.getAttribute('data-is-global-space-missing') === 'true';
	const microsEnvType = body.getAttribute('data-micros-env-type');
	const tenantId = body.getAttribute('data-tenant-id');
	const microsPerimeter = body.getAttribute('data-micros-perimeter');
	const settings = JSON.parse(window.name);
	const hostBaseUrl = settings.options.hostOrigin;
	const hostUrl = hostBaseUrl + settings.options.contextPath;
	const userID = settings.options.user.uid;

	const href = getHref();
	const params = Url.parse(href, true).query;

	const isLicensed = params.lic === 'active';
	// Empty filter means we are using macro which defaults to popular. Missing filter means app which defaults to recent
	const filter =
		params.filter === '' ? POPULAR : params.filter === undefined ? RECENT : params.filter;

	const microsBaseUrl = getOrigin();

	return {
		initialAddonTokenHeader,
		spaceKey,
		isGlobalQuestionsPage,
		globalSpaceMissing,
		hostUrl,
		hostBaseUrl,
		microsBaseUrl,
		mentionsUrl,
		mentionsProductId,
		tokenEarlyExpiry,
		userID,
		isLicensed,
		microsEnvType,
		microsPerimeter,
		filter,
		tenantId,
	};
});

const getCurrentUser = () => new Promise((resolve) => window.AP.getCurrentUser(resolve));
const getLocation = () =>
	new Promise((resolve) => {
		AP.getLocation((location) => {
			const urlData = Url.parse(location, true);
			resolve({
				...urlData,
			});
		});
	});

export const configureStore = () => {
	return Promise.all([getCurrentUser(), getLocation()]).then(([currentUser, topWindowLocation]) => {
		const { atlassianAccountId } = currentUser;
		const initialPath =
			topWindowLocation.query['ac.com.atlassian.confluence.plugins.confluence-questions.path'];

		const initialStateConfig = getAppConfig();
		initialStateConfig.accountId = atlassianAccountId;

		return configureStoreInternal({
			initialPath,
			initialStateConfig,
			appReducers,
			composeEnhancers: window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__,
		});
	});
};
